import { useRef, useState } from "react";

import { marketplaceApi } from "../api/client";
import MarketplaceHeader from "../components/MarketplaceHeader";
import ProductCard from "../components/ProductCard";
import FloatingTools from "../components/FloatingTools";
import Footer from "../components/Footer";

export default function VisualSearch() {
  const inputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");
  const [result, setResult] = useState(null);
  const [hint, setHint] = useState("");
  const [loading, setLoading] = useState(false);
  const [hintLoading, setHintLoading] = useState(false);
  const [error, setError] = useState("");

  function chooseImage(event) {
    const file = event.target.files?.[0];

    if (!file) return;

    setImage(file);
    setResult(null);
    setError("");
    setHint("");

    if (preview) {
      URL.revokeObjectURL(preview);
    }

    setPreview(URL.createObjectURL(file));
  }

  async function waitForMinimum(startedAt, minimumMs) {
    const elapsed = Date.now() - startedAt;

    if (elapsed < minimumMs) {
      await new Promise((resolve) =>
        setTimeout(resolve, minimumMs - elapsed)
      );
    }
  }

  async function scanProduct() {
    if (!image || loading) return;

    setLoading(true);
    setResult(null);
    setError("");

    const startedAt = Date.now();
    const minimumScanMs = 2600;

    try {
      await new Promise((resolve) =>
        requestAnimationFrame(() =>
          requestAnimationFrame(resolve)
        )
      );

      const response = await marketplaceApi.visualSearch(image);

      await waitForMinimum(startedAt, minimumScanMs);

      setResult(response.data || {});
    } catch (err) {
      await waitForMinimum(startedAt, minimumScanMs);

      setResult({
        ai_available: false,
        requires_hint: true,
        products: [],
        count: 0,
        message:
          "We couldn't identify the product from the photo this time. Enter the product name, brand or model below and TAZY will search current marketplace listings for you.",
      });
    } finally {
      setLoading(false);
    }
  }

  async function searchDatabase(event) {
    event?.preventDefault();

    const q = hint.trim();

    if (!q || hintLoading) return;

    setHintLoading(true);
    setError("");

    try {
      const response = await marketplaceApi.search({
        q,
        tab: "Products",
      });

      const products = response.data?.products || [];

      setResult((current) => ({
        ...(current || {}),
        ai_available: current?.ai_available ?? false,
        requires_hint: true,
        database_hint: q,
        products: products.map((item) => ({
          ...item,
          match_type: "DATABASE MATCH",
        })),
        count: products.length,
        message: products.length
          ? `${products.length} live product(s) matching "${q}" found on TAZY CART.`
          : `No live product matching "${q}" was found on TAZY CART.`,
      }));
    } catch (err) {
      setError(
        "TAZY could not search the marketplace database right now. Please try again."
      );
    } finally {
      setHintLoading(false);
    }
  }

  const identified = result?.identified || {};
  const products = result?.products || [];

  const identity = [
    identified.brand,
    identified.model,
    identified.label,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="page-shell marketplace-page">
      <style>{`
        .tazy-scan-preview {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 280px;
          background: #F6F7F9;
          border-radius: 18px;
          overflow: hidden;
          padding: 12px;
        }

        .tazy-scan-preview.scanning img {
          filter: brightness(.66);
        }

        .tazy-scan-overlay {
          position: absolute;
          inset: 0;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(7, 27, 61, .22);
          backdrop-filter: blur(1px);
        }

        .tazy-scan-wave {
          position: absolute;
          left: 4%;
          width: 92%;
          height: 3px;
          border-radius: 999px;
          background: linear-gradient(
            90deg,
            transparent,
            #FF6500,
            #FFFFFF,
            #FF6500,
            transparent
          );
          box-shadow:
            0 0 12px rgba(255, 101, 0, .95),
            0 0 28px rgba(255, 101, 0, .58);
          animation: tazyScanSweep 1.8s linear infinite;
        }

        .tazy-scan-wave.wave-two {
          opacity: .55;
          animation-delay: .22s;
        }

        .tazy-scan-wave.wave-three {
          opacity: .25;
          animation-delay: .44s;
        }

        .tazy-scan-status {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 5px;
          padding: 16px 22px;
          border-radius: 18px;
          background: rgba(7, 27, 61, .9);
          color: white;
          text-align: center;
          box-shadow: 0 12px 35px rgba(0,0,0,.25);
        }

        .tazy-scan-status small {
          max-width: 300px;
          color: #D7E1F3;
        }

        .tazy-scan-spinner {
          color: #FF8A1D;
          font-size: 28px;
          animation: tazyScanPulse 1s ease-in-out infinite;
        }

        .tazy-match-wrap {
          position: relative;
        }

        .tazy-match-badge {
          position: absolute;
          z-index: 3;
          top: 10px;
          left: 10px;
          padding: 6px 9px;
          border-radius: 999px;
          background: #071B3D;
          color: #fff;
          font-size: 10px;
          font-weight: 900;
          letter-spacing: .35px;
          box-shadow: 0 4px 12px rgba(0,0,0,.16);
        }

        .tazy-db-fallback {
          margin-top: 18px;
          padding: 18px;
          border: 1px solid #E7E9EE;
          border-radius: 18px;
          background: #FFF8F3;
        }

        .tazy-db-fallback form {
          display: flex;
          gap: 9px;
          margin-top: 12px;
        }

        .tazy-db-fallback input {
          flex: 1;
          min-width: 0;
          border: 1px solid #D7DBE3;
          border-radius: 12px;
          padding: 12px 14px;
          font-size: 15px;
        }

        .tazy-db-fallback button {
          border: 0;
          border-radius: 12px;
          padding: 12px 18px;
          background: #FF6500;
          color: #fff;
          font-weight: 800;
          cursor: pointer;
        }

        @keyframes tazyScanSweep {
          0% { top: 4%; }
          50% { top: 94%; }
          100% { top: 4%; }
        }

        @keyframes tazyScanPulse {
          0%, 100% {
            transform: scale(.9);
            opacity: .65;
          }

          50% {
            transform: scale(1.2);
            opacity: 1;
          }
        }

        @media (max-width: 640px) {
          .tazy-db-fallback form {
            flex-direction: column;
          }
        }
      `}</style>

      <MarketplaceHeader />

      <main>
        <section className="market-hero market-hero--clean">
          <div className="container hero-inner">
            <div>
              <span className="eyebrow">Visual product search</span>
              <h1>Find it on TAZY CART</h1>
              <p>
                Take a photo or upload an image. TAZY identifies useful
                product details and matches them only against live products
                in the TAZY CART marketplace.
              </p>
            </div>
          </div>
        </section>

        <section
          className="container"
          style={{
            maxWidth: 950,
            paddingTop: 32,
            paddingBottom: 40,
          }}
        >
          <div
            style={{
              background: "#fff",
              border: "1px solid #E7E9EE",
              borderRadius: 22,
              padding: 24,
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={chooseImage}
              style={{ display: "none" }}
            />

            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={chooseImage}
              style={{ display: "none" }}
            />

            {!preview ? (
              <div className="tazy-photo-source">
                <div className="tazy-photo-source__icon">⌖</div>

                <strong>Scan a product</strong>
                <p>Take a new photo or choose one from your phone.</p>

                <div className="tazy-photo-source__actions">
                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                  >
                    📷 Take photo
                  </button>

                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                  >
                    🖼 Choose from photos
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div
                  className={
                    loading
                      ? "tazy-scan-preview scanning"
                      : "tazy-scan-preview"
                  }
                >
                  <img
                    src={preview}
                    alt="Product to search"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 360,
                      objectFit: "contain",
                      borderRadius: 14,
                    }}
                  />

                  {loading ? (
                    <div className="tazy-scan-overlay">
                      <div className="tazy-scan-wave wave-one" />
                      <div className="tazy-scan-wave wave-two" />
                      <div className="tazy-scan-wave wave-three" />

                      <div className="tazy-scan-status">
                        <span className="tazy-scan-spinner">⌖</span>
                        <strong>Scanning product...</strong>
                        <small>
                          Identifying the item and matching it against
                          live TAZY CART products
                        </small>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    marginTop: 16,
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    type="button"
                    onClick={scanProduct}
                    disabled={loading}
                    className="create-account"
                  >
                    {loading ? "Scanning..." : "Search this product"}
                  </button>

                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    disabled={loading}
                  >
                    Take photo
                  </button>

                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={loading}
                  >
                    Photo library
                  </button>
                </div>
              </>
            )}

            {result?.requires_hint ? (
              <div className="tazy-db-fallback">
                <strong>Find it on TAZY CART</strong>
                <p>
                  {result.message ||
                    "Enter the product name, brand or model below and TAZY will search current marketplace listings for you."}
                </p>

                <form onSubmit={searchDatabase}>
                  <input
                    value={hint}
                    onChange={(event) => setHint(event.target.value)}
                    placeholder="e.g. laptop, HP laptop, EliteBook"
                  />

                  <button
                    type="submit"
                    disabled={hintLoading || !hint.trim()}
                  >
                    {hintLoading ? "Searching..." : "Search TAZY"}
                  </button>
                </form>
              </div>
            ) : null}

            {error ? (
              <div className="error-box" style={{ marginTop: 18 }}>
                {error}
              </div>
            ) : null}
          </div>

          {result && !result.requires_hint ? (
            <section style={{ marginTop: 30 }}>
              <div className="section-heading">
                <div>
                  <h2>
                    {identity
                      ? `TAZY identified: ${identity}`
                      : "Visual search results"}
                  </h2>

                  <p>{result.message}</p>
                </div>
              </div>

              {products.length ? (
                <div className="product-grid product-grid--compact">
                  {products.map((product) => (
                    <div
                      className="tazy-match-wrap"
                      key={product.id || product.slug}
                    >
                      <span className="tazy-match-badge">
                        {product.match_type || "MATCH"}
                      </span>

                      <ProductCard item={product} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="marketplace-empty-feed">
                  No matching live product is currently listed on TAZY CART.
                </div>
              )}
            </section>
          ) : null}

          {result?.requires_hint && products.length ? (
            <section style={{ marginTop: 30 }}>
              <div className="section-heading">
                <div>
                  <h2>TAZY marketplace matches</h2>
                  <p>{result.message}</p>
                </div>
              </div>

              <div className="product-grid product-grid--compact">
                {products.map((product) => (
                  <div
                    className="tazy-match-wrap"
                    key={product.id || product.slug}
                  >
                    <span className="tazy-match-badge">
                      {product.match_type || "DATABASE MATCH"}
                    </span>

                    <ProductCard item={product} />
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </section>
      </main>

      <FloatingTools />
      <Footer />
    </div>
  );
}
